import React, { ReactNode } from 'react';
import { Flex } from 'glints-aries/es';
import {
  OutlineButton,
  PrimaryButton,
  Typography,
} from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { CompanyStatus } from '../../../../common/constants';
import { useGetSessionCompany } from '../../../../common/hooks/useGetSessionCompany';
import { EmployerPurchaseProductType } from '../../../../common/interfaces/entities';
import { ROUTES } from '../../../../common/routes';
import { ConditionalProductUsageComponent } from '../../SharedComponents/ConditionalProductUsageComponent';
import { UnverifiedCompanyProductUsageWarningBanner } from '../../SharedComponents/UnverifiedCompanyProductUsageWarningBanner';
import { CreditProductPurchaseOrder } from '../../types';
import {
  ProductIcon,
  StyledButtonGroup,
  SuccessModalIntro,
  SuccessModalLink,
  SuccessModalSummary,
} from './styles.sc';
import { TermsAgreementText } from './TermsAgreementModal';

const messages = defineMessages({
  unlocksAdded: {
    id: 'text-unlocks-added',
    defaultMessage: 'Unlocks Added',
  },
  purchaseSuccessfully: {
    id: 'text-purchased-successfully',
    defaultMessage: 'Purchased successfully',
  },
  yourCVFinder: {
    id: 'text-your-cv-finder-unlocks',
    defaultMessage:
      'Your CV Finder unlocks will be valid till <bold>{date}.</bold>',
  },
  nowYouHaveUnlocks: {
    id: 'text-now-you-have-unlocks',
    defaultMessage:
      'Now you have <b>{amount}</b> unlocks. Check your <link1>unlock balance</link1> for more details.',
  },
});

type Props = {
  creditProductPurchaseOrder: Pick<
    CreditProductPurchaseOrder,
    'companyProductInventory'
  >;
  onClose: () => void;
};
const ModalTitle = () => (
  <FormattedMessage {...messages.purchaseSuccessfully} />
);

const ModalFooter = ({ onClose }: { onClose: () => void }) => {
  const { pathname } = useLocation();
  const talentSearchRegExp = RegExp(`^/talent-search$`);
  const isTalentSearchPath = talentSearchRegExp.test(pathname);
  const { sessionCompany } = useGetSessionCompany();
  const isCompanyVerified = sessionCompany?.status === CompanyStatus.VERIFIED;
  return (
    <StyledButtonGroup>
      <OutlineButton onClick={onClose}>
        <Typography variant="button">
          <FormattedMessage
            id="interactive-continue"
            defaultMessage="Continue"
          />
        </Typography>
      </OutlineButton>
      <If condition={!isTalentSearchPath}>
        <ConditionalProductUsageComponent
          isCompanyVerified={isCompanyVerified}
          destination={ROUTES.talentSearch}
        >
          <PrimaryButton onClick={onClose} disabled={!isCompanyVerified}>
            <Typography variant="button">
              <FormattedMessage
                id="interactive-explore-cv-finder-talent"
                defaultMessage="Explore CV Finder"
              />
            </Typography>
          </PrimaryButton>
        </ConditionalProductUsageComponent>
      </If>
    </StyledButtonGroup>
  );
};

const Bold = (...chunks: ReactNode[]) => (
  <Typography variant="caption" as="span">
    {chunks}
  </Typography>
);

function TalentSearchSuccessModalContentComponent({
  creditProductPurchaseOrder: { companyProductInventory },
  onClose,
}: Props) {
  const { formatDate, formatNumber } = useIntl();
  return (
    <Flex
      flexDirection="column"
      style={{
        gap: 20,
      }}
    >
      <SuccessModalIntro>
        <ProductIcon
          src="/images/credit-system/talent-search-icon.svg"
          alt="CV Finder"
        />
        <SuccessModalSummary>
          <Typography variant="body2">
            <FormattedMessage {...messages.unlocksAdded} />
          </Typography>
          <Typography variant="subtitle2" as="div" color={Neutral.B40}>
            <FormattedMessage
              {...messages.yourCVFinder}
              values={{
                date: companyProductInventory?.expiryDateTime
                  ? formatDate(companyProductInventory.expiryDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                    })
                  : '-',
                bold: Bold,
              }}
            />
          </Typography>
          <Typography variant="subtitle2" as="div" color={Neutral.B40}>
            <FormattedMessage
              {...messages.nowYouHaveUnlocks}
              values={{
                amount: formatNumber(companyProductInventory?.amount || 0),
                b: Bold,
                link1: (chunks: React.ReactNode) => (
                  <SuccessModalLink
                    to={`/${ROUTES.features}?tab=PURCHASES&purchasedTab=${EmployerPurchaseProductType.TALENT_SEARCH}`}
                    onClick={onClose}
                  >
                    {chunks}
                  </SuccessModalLink>
                ),
              }}
            />
          </Typography>
        </SuccessModalSummary>
      </SuccessModalIntro>
      <UnverifiedCompanyProductUsageWarningBanner />
      <TermsAgreementText />
    </Flex>
  );
}

export const TalentSearchSuccessModalContent = Object.assign(
  TalentSearchSuccessModalContentComponent,
  {
    ModalTitle,
    ModalFooter,
  }
);
